import { jsxs, jsx } from 'react/jsx-runtime';
import { isNil } from '@flex-design-system/fx-utils';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Primitive } from '@radix-ui/react-primitive';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import usePopperPresence from '../../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../../../hooks/usePreventTransitionInteraction.mjs';
import { overlayStyle } from '../../../../utils/overlay.vanilla.mjs';
import InjectAside from '../../../utility/InjectAside.mjs';
import { useModalContext } from '../../context.mjs';
import { StyledContent } from '../../styled.mjs';
import { contentPositioner } from '../../styles.vanilla.mjs';
import { contentRoot, content } from './content.vanilla.mjs';

const ModalContent = forwardRef(
  (props, forwardedRef) => {
    const {
      children,
      className,
      leftAside,
      rightAside,
      placement = "center",
      overlayClassName,
      overlayTheme = "light",
      animation = "default",
      css: cssFromProps,
      style: styleFromProps,
      portalContainer: portalContainerFromProps,
      zIndex,
      initialFocusElement,
      onOpenAutoFocus: onOpenAutoFocusFromProps,
      ...restProps
    } = props;
    const {
      modal,
      open,
      onClose,
      hasLeftAsideChildren,
      hasRightAsideChildren,
      onChangeModalContentElement,
      portalContainerElement: portalContainerElementFromContext,
      setScrollPos
    } = useModalContext("Modal.Content");
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (isPresent) => {
        if (isPresent) {
          return;
        }
        onClose?.();
        setScrollPos(0);
      }
    });
    const composedRef = useComposedRefs(
      forwardedRef,
      presenceRef,
      onChangeModalContentElement
    );
    const zIndexStyle = usePopperZIndexStyle(open, zIndex);
    const portalContainer = portalContainerFromProps ?? portalContainerElementFromContext;
    const positionStyle = useMemo(() => {
      if (isNil(portalContainer)) {
        return;
      }
      return { position: "absolute" };
    }, [portalContainer]);
    const handleOpenAutoFocus = composeEventHandlers(
      onOpenAutoFocusFromProps,
      (event) => {
        const restrictedFocusElement = typeof initialFocusElement === "function" ? initialFocusElement() : initialFocusElement;
        if (!isNil(restrictedFocusElement)) {
          restrictedFocusElement.focus();
          event.preventDefault();
        }
      }
    );
    const positionerStyle = Object.assign({}, zIndexStyle, positionStyle);
    return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { container: portalContainer, children: [
      /* @__PURE__ */ jsx(
        DialogPrimitive.Overlay,
        {
          className: classNames(
            overlayClassName,
            overlayStyle({ overlayTheme })
          ),
          style: positionerStyle
        }
      ),
      /* @__PURE__ */ jsx(
        Primitive.div,
        {
          className: contentPositioner({ placement, modal }),
          style: positionerStyle,
          children: /* @__PURE__ */ jsxs(
            StyledContent,
            {
              css: cssFromProps,
              className: classNames(content({ animation }), className),
              style: { ...styleFromProps, ...preventTransitionInteraction.style },
              ref: composedRef,
              onOpenAutoFocus: handleOpenAutoFocus,
              ...restProps,
              onKeyDownCapture: (e) => {
                preventTransitionInteraction.onKeyDownCapture?.(e);
                restProps.onKeyDownCapture?.(e);
              },
              children: [
                leftAside ? /* @__PURE__ */ jsx(InjectAside, { side: "left", children: leftAside }) : null,
                /* @__PURE__ */ jsx(
                  Primitive.div,
                  {
                    className: contentRoot({
                      hasAsideChild: hasLeftAsideChildren || hasRightAsideChildren
                    }),
                    children
                  }
                ),
                rightAside ? /* @__PURE__ */ jsx(InjectAside, { side: "right", children: rightAside }) : null
              ]
            }
          )
        }
      )
    ] });
  }
);

export { ModalContent };
//# sourceMappingURL=Content.mjs.map
